import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";

const LineToolBar = ({
  questionId,
  isLeftSidebarOpen,
  leftSidebarWidth = 0,
  isRightSidebarOpen,
  windowWidth,
  selectedLine,
  onComponentUpdate,
  onDeselect,
}) => {
  const [isMobile, setIsMobile] = useState(windowWidth < 768);
  const [strokeWidthInput, setStrokeWidthInput] = useState(
    String(selectedLine?.strokeWidth ?? 1)
  );
  // Update states when window size changes
  useEffect(() => {
    setIsMobile(windowWidth < 768);
  }, [windowWidth]);
  useEffect(() => {
    setStrokeWidthInput(String(selectedLine?.strokeWidth ?? 1));
  }, [selectedLine?.strokeWidth]);
  // Calculate offset for proper centering
  const calculatePosition = () => {
    // For mobile, just center in viewport
    if (windowWidth < 768) {
      return {
        left: "50%",
        transform: "translateX(-50%)",
      };
    }

    // For desktop, account for both sidebars
    let leftOffset = "50%";

    // Calculate sidebar offsets
    const leftSidebarOffset = isLeftSidebarOpen ? leftSidebarWidth / 2 : 0;
    const rightSidebarOffset = isRightSidebarOpen ? -32 : 0; // 64px / 2

    // Combine offsets
    const totalOffset = leftSidebarOffset + rightSidebarOffset;

    if (totalOffset !== 0) {
      leftOffset = `calc(50% + ${totalOffset}px)`;
    }

    return {
      left: leftOffset,
      transform: "translateX(-50%)",
    };
  };

  const position = calculatePosition();

  const clampNumber = (value, min, max, fallback) => {
    const parsed = parseInt(String(value), 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
  };

  const commitStrokeWidth = () => {
    const clamped = clampNumber(strokeWidthInput, 1, 10, 1);
    setStrokeWidthInput(String(clamped));
    if (selectedLine?.strokeWidth !== clamped) {
      onComponentUpdate(questionId, selectedLine.id, { strokeWidth: clamped });
    }
  };

  const resetStrokeWidthInput = () => {
    setStrokeWidthInput(String(selectedLine?.strokeWidth ?? 1));
  };

  return (
    <div
      className="fixed z-50 transition-all duration-300"
      style={{
        left: position.left,
        transform: position.transform,
        bottom: isMobile ? "calc(34px + env(safe-area-inset-bottom, 0px))" : 34,
      }}
    >
      <motion.div
        key={questionId}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className={``}
      >
        <div
          className={`bg-white rounded-xl shadow-xl border border-gray-200 relative ${
            isMobile ? "p-2" : "p-3"
          }`}
          style={{
            width: isMobile ? "calc(100vw - 24px)" : "600px",
            maxWidth: "calc(100vw - 24px)",
            maxHeight: isMobile ? "34vh" : "none",
            overflowY: isMobile ? "auto" : "visible",
          }}
        >
          <button
            onClick={onDeselect}
            className={`${
              isMobile ? "top-2 right-2" : "-top-3 right-2"
            } absolute bg-white rounded-full ${
              isMobile ? "p-1" : "p-1.5"
            } shadow-md border border-gray-200`}
            aria-label="Close"
            title="Close"
          >
            <X size={isMobile ? 14 : 16} className="text-gray-700" />
          </button>
          <div>
            <div
              style={{
                minWidth: 200,
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(auto-fit, minmax(120px, 1fr))"
                  : "repeat(3, minmax(0, 1fr))",
                gap: isMobile ? 8 : 12,
              }}
            >
              {/* Line Style */}
              <label className="w-full">
                <span className="text-[10px] font-medium text-gray-700">
                  Style:
                </span>
                <select
                  value={selectedLine?.shapeType}
                  onChange={(e) =>
                    onComponentUpdate(questionId, selectedLine.id, {
                      shapeType: e.target.value,
                    })
                  }
                  className="w-full rounded-md border border-gray-300 bg-white h-7 py-1 px-2 text-[10px] leading-tight text-gray-800 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                >
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                  <option value="dotted">Dotted</option>
                </select>
              </label>

              {/* Stroke Width */}
              <label className="w-full">
                <span className="text-[10px] font-medium text-gray-700">
                  Width:
                </span>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={strokeWidthInput}
                  onChange={(e) => {
                    const next = e.target.value;
                    setStrokeWidthInput(next);
                    const parsed = parseInt(next, 10);
                    if (
                      Number.isFinite(parsed) &&
                      parsed >= 1 &&
                      parsed <= 10 &&
                      selectedLine?.strokeWidth !== parsed
                    ) {
                      onComponentUpdate(questionId, selectedLine.id, {
                        strokeWidth: parsed,
                      });
                    }
                  }}
                  onBlur={commitStrokeWidth}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitStrokeWidth();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      resetStrokeWidthInput();
                    }
                  }}
                  className="h-7 w-full rounded-md border border-gray-300 bg-white py-1 px-2 text-[10px] leading-tight text-gray-800 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                />
              </label>

              {/* Stroke Color */}
              <label className="w-full">
                <span className="text-[10px] font-medium text-gray-700">
                  Color:
                </span>
                <input
                  type="color"
                  value={selectedLine?.strokeColor}
                  onChange={(e) =>
                    onComponentUpdate(questionId, selectedLine.id, {
                      strokeColor: e.target.value,
                    })
                  }
                  className="h-7 w-full rounded border border-gray-300 p-0 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                />
              </label>

              {/* Arrow Type */}
              <label className="w-full">
                <span className="text-[10px] font-medium text-gray-700">
                  Arrows:
                </span>
                <select
                  value={selectedLine?.arrowType || "both"}
                  onChange={(e) =>
                    onComponentUpdate(questionId, selectedLine.id, {
                      arrowType: e.target.value,
                    })
                  }
                  className="block w-full rounded-md border border-gray-300 bg-white h-7 py-1 px-2 text-[10px] leading-tight text-gray-800 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                >
                  <option value="none">None</option>
                  <option value="start">Start</option>
                  <option value="end">End</option>
                  <option value="both">Both</option>
                </select>
              </label>

              {/* Handle Shape */}
              <label className="w-full">
                <span className="text-[10px] font-medium text-gray-700">
                  Handle Shape:
                </span>
                <select
                  value={selectedLine?.endpointShape}
                  onChange={(e) =>
                    onComponentUpdate(questionId, selectedLine.id, {
                      endpointShape: e.target.value,
                    })
                  }
                  className="block w-full rounded-md border border-gray-300 bg-white h-7 py-1 px-2 text-[10px] leading-tight text-gray-800 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                >
                  <option value="circle">Circle</option>
                  <option value="square">Square</option>
                  <option value="arrow">Arrow</option>
                </select>
              </label>

              {/* Handle Color */}
              <label className="w-full">
                <span className="text-[10px] font-medium text-gray-700">
                  Handle Color:
                </span>
                <input
                  type="color"
                  value={selectedLine?.endpointColor || "#e11d48"}
                  onChange={(e) =>
                    onComponentUpdate(questionId, selectedLine.id, {
                      endpointColor: e.target.value,
                    })
                  }
                  className="h-7 w-full rounded border border-gray-300 p-0 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                />
              </label>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default LineToolBar;
